import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Form,
  InputGroup,
  Badge,
  Button,
  Spinner,
  Alert,
  Pagination
} from 'react-bootstrap';

import { FaSearch, FaFilter, FaEye } from 'react-icons/fa';
import { Modal } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import referralService from '../services/referralService';
import { useAuth } from '../context/AuthContext';

function ReferralPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const { isAdmin } = useAuth();
  const [applyingRewards, setApplyingRewards] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultMessage, setResultMessage] = useState('');

  const itemsPerPage = 20;

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: currentPage,
        per_page: itemsPerPage
      };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;

      // Try to fetch user's referrals
      const data = await referralService.getMy(params).catch(async () => {
        // Fallback: try admin endpoint
        return referralService.getAll(params);
      });

      // Expecting { referrals: [...], total, page, per_page, pages }
      const rows = data.referrals || data.items || [];
      setItems(rows);
      setTotalItems(data.total || rows.length);
      setTotalPages(data.pages || 1);
    } catch (err) {
      setError('Failed to load referrals');
      console.error('Referral fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Handler to apply missing rewards (called from confirm modal)
  const handleApplyRewards = async () => {
    try {
      setShowConfirmModal(false);
      setApplyingRewards(true);
      const res = await referralService.applyMissingRewards();
      setResultMessage(`Applied rewards to ${res.applied} referrals`);
      setShowResultModal(true);
      setCurrentPage(1);
      fetchItems();
    } catch (err) {
      console.error('Apply rewards error', err);
      setResultMessage('Failed to apply rewards');
      setShowResultModal(true);
    } finally {
      setApplyingRewards(false);
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const getStatusVariant = (status) => {
    const variants = {
      Pending: 'warning',
      Verified: 'info',
      Completed: 'success',
      Cancelled: 'secondary'
    };
    return variants[status] || 'secondary';
  };

  if (loading && items.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader title="Referrals" subtitle={`${totalItems} referrals`} />

      <Row className="g-3 mb-4">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaSearch className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </InputGroup>
        </Col>
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaFilter className="text-muted" />
            </InputGroup.Text>
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Verified">Verified</option>
              <option value="Completed">Completed</option>
            </Form.Select>
          </InputGroup>
        </Col>
        {isAdmin && (
          <Col md={2}>
            <Button
              variant="primary"
              className="w-100"
              disabled={applyingRewards}
              onClick={() => setShowConfirmModal(true)}
            >
              Apply Rewards
            </Button>
          </Col>
        )}
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Confirm Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Apply Referral Rewards</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to apply missing referral rewards now? This will credit referrers for completed referrals.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleApplyRewards} disabled={applyingRewards}>{applyingRewards ? 'Applying…' : 'Apply'}</Button>
        </Modal.Footer>
      </Modal>

      {/* Result Modal */}
      <Modal show={showResultModal} onHide={() => setShowResultModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Result</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{resultMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowResultModal(false)}>OK</Button>
        </Modal.Footer>
      </Modal>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                    <th className="fw-semibold">ID</th>
                    <th className="fw-semibold">Referrer</th>
                    <th className="fw-semibold">Invited</th>
                  <th className="fw-semibold">Origin</th>
                  <th className="fw-semibold">Status</th>
                  <th className="fw-semibold">Invited At</th>
                  <th className="fw-semibold">Registered At</th>
                  <th className="fw-semibold text-end">Reward</th>
                  <th className="fw-semibold text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <p className="text-muted mb-0">No referrals found</p>
                    </td>
                  </tr>
                ) : (
                  items.map((r) => (
                    <tr key={r.id}>
                      <td>#{r.id}</td>
                      <td>
                        <div className="fw-semibold">{r.referrer ? r.referrer.username : '-'}</div>
                        <small className="text-muted">{r.referrer ? `#${r.referrer.id}` : ''}</small>
                      </td>
                      <td>
                        <div className="fw-semibold">{r.invited_name || r.invited_email}</div>
                        <small className="text-muted">{r.invited_email}</small>
                      </td>
                      <td>{r.origin}</td>
                      <td>
                        <Badge bg={getStatusVariant(r.status)}>{r.status}</Badge>
                      </td>
                      <td>
                        <small className="text-muted">{r.invited_at ? new Date(r.invited_at).toLocaleString() : '-'}</small>
                      </td>
                      <td>
                        <small className="text-muted">{r.registered_at ? new Date(r.registered_at).toLocaleString() : '-'}</small>
                      </td>
                      <td className="text-end">{r.reward ? `${parseInt(r.reward, 10)} pts` : '-'}</td>
                      <td className="text-end">
                        <Button variant="link" size="sm" className="text-primary p-1" onClick={() => navigate(`/referrals/${r.id}`)}>
                          <FaEye />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                return (
                  <Pagination.Item key={page} active={page === currentPage} onClick={() => handlePageChange(page)}>
                    {page}
                  </Pagination.Item>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <Pagination.Ellipsis key={page} disabled />;
              }
              return null;
            })}
            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}
    </Container>
  );
}

export default ReferralPage;
